/**
 * Landing.tsx — Public marketing page.
 *
 * Route: / (root) — shown only to unauthenticated visitors.
 * Authenticated users are redirected to /dashboard in App.tsx.
 *
 * DO NOT change: routing, auth flow, signup flow, or any backend calls.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../config/api';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VerifiedIcon from '@mui/icons-material/Verified';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// ── Plan data fetched from /api/plans ─────────────────────────────────────────

const NAV_LINKS = [
  { label: 'Home',     action: 'home' },
  { label: 'Features', action: 'features' },
  { label: 'Pricing',  action: 'pricing-section' },
  { label: 'Plans',    action: 'pricing-page' },
  { label: 'Contact',  action: 'contact' },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function TrustBadge({ label }: { label: string }) {
  return (
    <Chip
      label={label}
      variant="outlined"
      size="small"
      icon={<VerifiedIcon style={{ fontSize: 13 }} />}
      sx={{
        borderColor: 'rgba(108,99,255,0.45)',
        color: 'primary.main',
        fontWeight: 700,
        fontSize: 11,
        '& .MuiChip-icon': { color: 'primary.main' },
      }}
    />
  );
}

function StatPill({ value, label }: { value: string; label: string }) {
  return (
    <Box sx={{ textAlign: 'center', px: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight={900} color="primary.main" sx={{ lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Box>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  accent: string;
  title: string;
  desc: string;
  badge?: string;
}

function FeatureCard({ icon, accent, title, desc, badge }: FeatureCardProps) {
  return (
    <MuiCard
      sx={{
        borderRadius: 3,
        border: `1px solid ${accent}22`,
        bgcolor: `${accent}07`,
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 32px ${accent}18` },
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: `${accent}18`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: accent, mb: 2, flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>{title}</Typography>
          {badge && (
            <Chip label={badge} size="small" sx={{ fontSize: 10, fontWeight: 700,
              bgcolor: `${accent}18`, color: accent, height: 20 }} />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {desc}
        </Typography>
      </CardContent>
    </MuiCard>
  );
}

function PainPoint({ emoji, text }: { emoji: string; text: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
      <Typography sx={{ fontSize: 26, flexShrink: 0, mt: 0.25 }}>{emoji}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>{text}</Typography>
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [plans, setPlans]   = useState<any[]>([]);
  const [annual, setAnnual] = useState(false);
  const trialDays = plans[0]?.trial_days ?? 14;

  useEffect(() => {
    axios.get(`${API_BASE}/plans`).then((r) => setPlans(r.data)).catch(console.error);
  }, []);

  function handleNav(action: string) {
    setDrawerOpen(false);
    if (action === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (action === 'features') {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    } else if (action === 'pricing-section') {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    } else if (action === 'pricing-page') {
      navigate('/pricing');
    } else if (action === 'contact') {
      navigate('/contact');
    }
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Sticky Navbar ────────────────────────────────────────────────── */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'light' ? '#ffffff' : 'rgba(13,17,23,0.92)',
          backdropFilter: 'blur(14px)',
          borderBottom: (theme) => theme.palette.mode === 'light' ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 5 }, minHeight: { xs: 56, sm: 64 } }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: 4 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <SecurityIcon sx={{ color: '#00E5FF', fontSize: 26, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
            <Typography variant="h6" fontWeight={800} noWrap letterSpacing={0.5}
              sx={{ color: '#00E5FF', textShadow: '0 0 10px rgba(0,229,255,0.4)' }}>
              Shield
            </Typography>
          </Box>

          {/* Desktop nav */}
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' }, flex: 1 }}>
            {NAV_LINKS.map((l) => (
              <Button key={l.label} onClick={() => handleNav(l.action)}
                sx={{ color: 'text.secondary', fontWeight: 500, '&:hover': { color: 'text.primary' } }}>
                {l.label}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <Button variant="text" onClick={() => navigate('/login')}
              sx={{
                color: (theme) => theme.palette.mode === 'light' ? '#1a1a2e' : '#fff',
                '&:hover': { opacity: 0.85 },
              }}>
              Sign in
            </Button>
            <Button variant="contained" onClick={() => navigate('/signup')} size="small"
              sx={{ fontWeight: 700, px: 2.5 }}>
              Start Free Trial
            </Button>
          </Stack>

          {/* Mobile hamburger */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, flex: 1, justifyContent: 'flex-end' }}>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: (theme) => theme.palette.mode === 'light' ? '#1a1a2e' : '#ffffff' }}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260, bgcolor: 'background.paper' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((l) => (
            <ListItem key={l.label} disablePadding>
              <ListItemButton onClick={() => handleNav(l.action)}>
                <ListItemText primary={l.label} />
              </ListItemButton>
            </ListItem>
          ))}
          <Divider sx={{ my: 1 }} />
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/login'); }}>
              <ListItemText primary="Sign in" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ px: 2, py: 1 }}>
            <Button variant="contained" fullWidth
              onClick={() => { setDrawerOpen(false); navigate('/signup'); }}
              sx={{ fontWeight: 700 }}>
              Start Free Trial
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          textAlign: 'center',
          pt: { xs: 6, md: 10 },
          pb: { xs: 4, md: 5 },
          px: 3,
          background: 'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(108,99,255,0.12) 0%, transparent 60%)',
        }}
      >
        <Chip
          label="Now with Compliance Autopilot — SOC2, GDPR, HIPAA, PCI-DSS, ISO 27001"
          size="small"
          sx={{
            mb: 3, bgcolor: 'rgba(108,99,255,0.12)', color: 'primary.main',
            border: '1px solid rgba(108,99,255,0.3)', fontWeight: 600, fontSize: 12,
          }}
        />

        <Typography
          variant="h1"
          fontWeight={900}
          sx={{
            fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
            mb: 2.5,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          Your cloud is probably{' '}
          <Box component="span" sx={{ color: 'error.main' }}>exposed</Box>
          {' '}right now.
        </Typography>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 640, mx: 'auto', mb: 4.5, fontWeight: 400, lineHeight: 1.65, fontSize: { xs: '1rem', md: '1.15rem' } }}
        >
          Shield scans AWS, GCP, Azure and on-premise servers — detects misconfigurations,
          shadow AI usage, and compliance gaps — then fixes them automatically.
          No security team needed.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" mb={5}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/signup')}
            sx={{ fontWeight: 700, px: 4.5, py: 1.7, fontSize: 16, borderRadius: 2 }}
          >
            Start Free Trial — {trialDays} days free
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            sx={{ fontWeight: 600, px: 4, borderRadius: 2 }}
          >
            See How It Works
          </Button>
        </Stack>

        {/* Trust badges */}
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" gap={1}>
          {['SOC2', 'GDPR', 'HIPAA', 'PCI-DSS', 'ISO 27001'].map((b) => (
            <TrustBadge key={b} label={b} />
          ))}
        </Stack>
      </Box>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <Box id="features" sx={{ py: { xs: 5, md: 7 }, px: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" mb={1.5}
          sx={{ fontSize: { xs: '1.7rem', md: '2.3rem' } }}>
          One platform. Every security layer covered.
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}
          sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.65 }}>
          Shield replaces five separate tools with a single automated security platform.
        </Typography>

        <Grid container spacing={3} maxWidth={1080} mx="auto">
          {/* Feature 1 — Scan & Auto-Fix */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<AutoFixHighIcon />}
              accent="#2ecc71"
              title="Scan & Auto-Fix"
              badge="Read-only by default"
              desc="Shield scans AWS, GCP, Azure and SSH using a read-only role — it can see your configuration but never change anything. If you choose to opt in, a second role with limited write permissions can auto-fix specific issues like closing an exposed S3 bucket or enabling missing logging. Fixes are scoped and visible to you — Shield never deletes data, touches application logic, or requests broad admin access."
            />
          </Grid>

          {/* Feature 2 — NHI Governance */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<VpnKeyIcon />}
              accent="#d4a843"
              title="Non-Human Identity Governance"
              desc="Tracks API keys, service accounts, and machine credentials across all connected clouds. Flags zombie identities inactive for 90+ days and shows first and last use per credential — so you can find and clean up forgotten or orphaned access before it becomes a breach vector."
            />
          </Grid>

          {/* Feature 3 — Compliance Autopilot */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<VerifiedIcon />}
              accent="#6c63ff"
              title="Compliance Autopilot"
              badge="New"
              desc="Automated scoring against SOC2, GDPR, HIPAA, PCI-DSS, and ISO 27001. Generate PDF audit reports for your auditors in one click — no consultants needed."
            />
          </Grid>

          {/* Feature 4 — Shadow AI Governance */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<SmartToyIcon />}
              accent="#f77f00"
              title="Shadow AI Governance"
              desc="Detects ChatGPT, Gemini, GitHub Copilot, Cursor, DeepSeek and 10+ other AI tools on your corporate network in real time. Know exactly what data your employees are sharing with AI services."
            />
          </Grid>

          {/* Feature 5 — Threat Intelligence */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<BugReportIcon />}
              accent="#ef233c"
              title="Threat Intelligence"
              badge="Live"
              desc="CVE database updated daily from NIST NVD. CISA Known Exploited Vulnerabilities feed. MITRE ATT&CK framework. All automatically cross-referenced against your infrastructure."
            />
          </Grid>

          {/* Feature 6 — Behavioral Fingerprinting */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<FingerprintIcon />}
              accent="#00b4d8"
              title="27-Dimension Behavioral Fingerprinting"
              desc="SNAP protocol analyses 27 risk dimensions per scan — network exposure, identity risk, data classification, encryption gaps, and more — to produce a precise, actionable risk score."
            />
          </Grid>

          {/* Feature 7 — Cloud coverage */}
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<SecurityIcon />}
              accent="#a8dadc"
              title="4-Cloud Coverage"
              desc="AWS, Google Cloud, Microsoft Azure and on-premise SSH servers — all in one dashboard. Connect in minutes with read-only credentials. No agents to install."
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Stats bar ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          py: { xs: 4, md: 5 },
          px: 3,
          bgcolor: 'rgba(255,255,255,0.02)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="center"
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
          flexWrap="wrap"
          gap={{ xs: 3, sm: 0 }}
        >
          <StatPill value="1,200+" label="Security checks per scan" />
          <StatPill value="16+"    label="AI tools monitored" />
          <StatPill value="5"      label="Compliance frameworks" />
          <StatPill value="Daily"  label="CVE + CISA KEV updates" />
          <StatPill value="4"      label="Cloud providers (AWS, GCP, Azure, SSH)" />
        </Stack>
      </Box>

      {/* ── Problem ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: 'rgba(239,35,60,0.03)',
          borderTop: '1px solid rgba(239,35,60,0.1)',
          borderBottom: '1px solid rgba(239,35,60,0.1)',
          py: { xs: 5, md: 7 },
          px: { xs: 3, md: 10 },
        }}
      >
        <Box maxWidth={740} mx="auto">
          <Typography
            variant="h4"
            fontWeight={800}
            textAlign="center"
            mb={5}
            sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}
          >
            Most companies are one misconfiguration away from a breach
          </Typography>
          <PainPoint emoji="🔓" text="Your S3 bucket or cloud storage might be publicly readable right now — and you'd have no way to know." />
          <PainPoint emoji="🤖" text="Your employees are pasting confidential data into ChatGPT, Gemini, Cursor, and 13+ other AI tools every single day." />
          <PainPoint emoji="📋" text="Your next SOC2 or GDPR audit is approaching and you have no idea if you actually pass — or what evidence to provide." />
          <PainPoint emoji="😰" text="You don't have a dedicated security team to monitor, find, or fix any of this." />
        </Box>
      </Box>

      <Divider />

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <Box id="how" sx={{ py: { xs: 5, md: 7 }, px: { xs: 2, sm: 4 }, bgcolor: 'rgba(255,255,255,0.01)' }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" mb={1}
          sx={{ fontSize: { xs: '1.7rem', md: '2.2rem' } }}>
          Up and running in under 5 minutes
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}
          sx={{ maxWidth: 500, mx: 'auto' }}>
          No agents. No complex setup. Connect your cloud and Shield handles the rest.
        </Typography>

        <Grid container spacing={4} maxWidth={900} mx="auto">
          {[
            { step: '01', title: 'Connect your cloud', desc: 'Add read-only API credentials for AWS, GCP, or Azure. On-premise SSH also supported.' },
            { step: '02', title: 'Shield scans automatically', desc: 'Over 1,200 security checks run immediately. Results appear in your dashboard within minutes.' },
            { step: '03', title: 'Fix with one click', desc: 'Every finding comes with a plain-English explanation and a one-click auto-fix button.' },
            { step: '04', title: 'Stay compliant, always', desc: 'Compliance scores update automatically. Download PDF audit reports for SOC2, GDPR, HIPAA, PCI-DSS or ISO 27001 anytime.' },
          ].map(({ step, title, desc }) => (
            <Grid item xs={12} sm={6} key={step}>
              <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2,
                  bgcolor: 'rgba(108,99,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ fontSize: 12 }}>
                    {step}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} mb={0.5}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider />

      {/* ── Security & Trust ─────────────────────────────────────────────── */}
      <Box sx={{ background: 'linear-gradient(135deg, #0A1628 0%, #0D2137 50%, #0A1628 100%)', py: { xs: 6, md: 8 }, px: { xs: 2, sm: 4 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 600, height: 300, background: 'radial-gradient(ellipse, rgba(0,229,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Typography variant="h4" fontWeight={800} textAlign="center" mb={1.5}
          sx={{ fontSize: { xs: '1.7rem', md: '2.3rem' }, color: '#FFFFFF' }}>
          Your credentials never leave your control
        </Typography>
        <Typography variant="body1" textAlign="center" mb={4}
          sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.65, color: 'rgba(255,255,255,0.6)' }}>
          Shield needs read-only access to scan your cloud.
          Here is exactly how we protect what you share with us.
        </Typography>

        <Grid container spacing={3} maxWidth={1080} mx="auto">
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 3, p: 3, height: '100%', '&:hover': { background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.4)', transform: 'translateY(-4px)', transition: 'all 0.2s ease' } }}>
              <Box sx={{ mb: 2, color: '#00E5FF', display: 'flex' }}>
                <LockIcon sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: '#FFFFFF' }}>Encrypted at rest</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'rgba(255,255,255,0.65)' }}>
                Every credential you connect is encrypted using AES-256 before being written to our database.
                We store ciphertext — not your actual keys.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 3, p: 3, height: '100%', '&:hover': { background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.4)', transform: 'translateY(-4px)', transition: 'all 0.2s ease' } }}>
              <Box sx={{ mb: 2, color: '#00E5FF', display: 'flex' }}>
                <VisibilityOffIcon sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: '#FFFFFF' }}>We cannot read your keys</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'rgba(255,255,255,0.65)' }}>
                Your credentials are decrypted only during an active scan and never logged, printed, or stored
                in plain text — not even by us.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 3, p: 3, height: '100%', '&:hover': { background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.4)', transform: 'translateY(-4px)', transition: 'all 0.2s ease' } }}>
              <Box sx={{ mb: 2, color: '#00E5FF', display: 'flex' }}>
                <ShieldIcon sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: '#FFFFFF' }}>Read-only access only</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'rgba(255,255,255,0.65)' }}>
                Shield only reads your cloud configuration.
                We never create, modify, or delete anything
                in your AWS, GCP, Azure, or SSH environment.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 3, p: 3, height: '100%', '&:hover': { background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.4)', transform: 'translateY(-4px)', transition: 'all 0.2s ease' } }}>
              <Box sx={{ mb: 2, color: '#00E5FF', display: 'flex' }}>
                <VerifiedIcon sx={{ fontSize: 36 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: '#FFFFFF' }}>SOC2 Type II architecture</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'rgba(255,255,255,0.65)' }}>
                Our security model is built to meet SOC2 Type II,
                GDPR, and HIPAA standards from day one —
                not added later.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* ── Pricing ──────────────────────────────────────────────────────── */}
      <Box id="pricing" sx={{ py: { xs: 5, md: 7 }, px: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" mb={1}
          sx={{ fontSize: { xs: '1.7rem', md: '2.2rem' } }}>
          Simple, honest pricing
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
          {trialDays}-day free trial on all plans — no credit card required
        </Typography>

        {/* Monthly / Annual toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 4 }}>
          <Typography variant="body2" color={annual ? 'text.secondary' : 'text.primary'} fontWeight={500}>Monthly</Typography>
          <Switch checked={annual} onChange={(e) => setAnnual(e.target.checked)} color="primary" />
          <Typography variant="body2" color={annual ? 'text.primary' : 'text.secondary'} fontWeight={500}>Annual</Typography>
          <Chip label={`Save ${plans[0]?.annual_discount_pct ?? 20}%`} size="small" color="success" sx={{ fontWeight: 700 }} />
        </Box>

        <Grid container spacing={3} maxWidth={1020} mx="auto" alignItems="stretch">
          {plans.map((plan) => {
            const displayPrice = annual ? plan.price_annually_monthly : plan.price_monthly;
            const annualSavings = annual ? Math.round(plan.annual_savings ?? 0) : 0;
            return (
              <Grid item xs={12} md={4} key={plan.plan_key}>
                <MuiCard
                  sx={{
                    borderRadius: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: plan.is_popular ? '2px solid' : '1px solid',
                    borderColor: plan.is_popular ? 'primary.main' : 'rgba(255,255,255,0.08)',
                    bgcolor: plan.is_popular ? 'rgba(108,99,255,0.05)' : 'background.paper',
                    position: 'relative',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-2px)' },
                  }}
                >
                  {plan.is_popular && (
                    <Box sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)' }}>
                      <Chip label="⭐ Most Popular" size="small" color="primary"
                        sx={{ fontWeight: 700, fontSize: 11 }} />
                    </Box>
                  )}

                  <CardContent sx={{ p: 3.5, pt: plan.is_popular ? 5 : 3.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight={700} mb={0.5}>{plan.display_name}</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2.5}>{plan.tagline ?? ''}</Typography>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h3" fontWeight={900} color="primary.main">
                          ${Math.round(displayPrice)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">/month</Typography>
                      </Box>
                      {annual && annualSavings > 0 && (
                        <Typography variant="caption" color="success.main" fontWeight={600}>
                          Billed annually — save ${annualSavings}/yr
                        </Typography>
                      )}
                    </Box>

                    <Button
                      fullWidth
                      variant={plan.is_popular ? 'contained' : 'outlined'}
                      onClick={() => navigate(`/signup?plan=${plan.plan_key}`)}
                      sx={{ fontWeight: 700, mb: 3, borderRadius: 2 }}
                    >
                      Start Free Trial
                    </Button>

                    <Divider sx={{ mb: 2.5 }} />

                    <Stack spacing={1} sx={{ flex: 1 }}>
                      {(plan.features as string[]).map((f) => (
                        <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0, mt: 0.2 }} />
                          <Typography variant="body2">{f}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </MuiCard>
              </Grid>
            );
          })}
        </Grid>

        {/* View full comparison */}
        <Box textAlign="center" mt={4}>
          <Button variant="text" onClick={() => navigate('/pricing')} sx={{ color: 'text.secondary' }}>
            View full plan comparison →
          </Button>
        </Box>
      </Box>

      {/* ── CTA Banner ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          mx: { xs: 2, sm: 4, md: 8 },
          mb: 8,
          borderRadius: 4,
          border: '1px solid rgba(108,99,255,0.25)',
          bgcolor: 'rgba(108,99,255,0.06)',
          textAlign: 'center',
          py: { xs: 6, md: 8 },
          px: 4,
          background: 'radial-gradient(ellipse 70% 80% at 50% 50%, rgba(108,99,255,0.1) 0%, transparent 70%)',
        }}
      >
        <Typography variant="h4" fontWeight={800} mb={2}
          sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>
          Start protecting your cloud today
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}
          sx={{ maxWidth: 500, mx: 'auto', lineHeight: 1.65 }}>
          Join hundreds of companies using Shield to stay secure and compliant — automatically.
          No credit card. No commitment. Cancel anytime.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/signup')}
            sx={{ fontWeight: 700, px: 5, py: 1.7, fontSize: 16, borderRadius: 2 }}
          >
            Start Free Trial
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{ fontWeight: 600, px: 4, borderRadius: 2 }}
          >
            Talk to Sales
          </Button>
        </Stack>
      </Box>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 5, px: { xs: 3, sm: 6 } }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 3,
            maxWidth: 1080,
            mx: 'auto',
          }}
        >
          {/* Brand */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SecurityIcon sx={{ color: '#00E5FF', fontSize: 22, filter: 'drop-shadow(0 0 5px rgba(0,229,255,0.55))' }} />
              <Typography variant="body1" fontWeight={800} sx={{ color: '#00E5FF', textShadow: '0 0 8px rgba(0,229,255,0.35)' }}>Shield</Typography>
            </Box>
            <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 260, lineHeight: 1.6 }}>
              B2B cloud security &amp; compliance automation for companies without a security team.
            </Typography>
          </Box>

          {/* Trust badges */}
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {['SOC2', 'GDPR', 'HIPAA', 'PCI-DSS', 'ISO 27001'].map((b) => (
              <TrustBadge key={b} label={b} />
            ))}
          </Stack>

          {/* Links */}
          <Stack direction="row" spacing={3} flexWrap="wrap">
            {[
              { label: 'Pricing',  path: '/pricing' },
              { label: 'Privacy',  path: '/privacy' },
              { label: 'Terms',    path: '/terms' },
              { label: 'Contact',  path: '/contact' },
            ].map(({ label, path }) => (
              <Typography
                key={label}
                variant="body2"
                color="text.disabled"
                onClick={() => navigate(path)}
                sx={{ cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
              >
                {label}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Typography variant="body2" color="text.disabled" textAlign="center" mt={4}>
          © {new Date().getFullYear()} Shield Security · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
